import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../api";
import { emailFromQueryParam } from "../../utils/emailFromQueryParam";
import collingsLogo from "../../assets/collingslogo.png";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const token = query.get("reset_token");
  const email = emailFromQueryParam(query.get("email"));
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
    black: "#000000",
    white: "#FFFFFF",
    lightGray: "#F8F9FA",
    mediumGray: "#E9ECEF",
    darkGray: "#495057",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      toast.error("Both fields are required.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/role-management/reset_password/", {
        reset_token: token,
        email,
        password,
      });
      toast.success("Password reset successful! You can now log in.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      let message = "Failed to reset password.";
      if (error.response && error.response.data && error.response.data.detail) {
        message = error.response.data.detail;
      } else if (error.message) {
        message = error.message;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4"
      style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
    >
      <div className="bg-white rounded-3xl shadow-2xl p-12 w-full max-w-lg border border-gray-100">
        <div className="text-center mb-10">
          <div className="flex flex-col items-center justify-center mb-6">
            <div className="mb-4">
              <img
                src={collingsLogo}
                alt="Collings Real Estate"
                className="h-8 sm:h-9 w-auto max-w-[180px] object-contain mx-auto"
              />
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Zap
                className="w-4 h-4"
                style={{ color: brandColors.secondary }}
              />
              <span>AI-Powered Lead Generation Platform</span>
            </div>
          </div>
          <p className="text-gray-600 font-light text-lg">
            Reset Your Password
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#55C5D0] text-sm transition-all duration-200 bg-gray-50 focus:bg-white pr-12"
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#55C5D0] focus:outline-none"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#55C5D0] text-sm transition-all duration-200 bg-gray-50 focus:bg-white pr-12"
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#55C5D0] focus:outline-none"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || !token || !email}
            className="w-full text-white py-4 px-6 rounded-xl hover:opacity-90 transition-all duration-300 font-medium text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            style={{
              backgroundColor: brandColors.secondary,
              opacity: loading || !token || !email ? 0.6 : 1,
            }}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
        <div className="mt-8 text-sm text-center">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-gray-600 hover:text-gray-900 font-medium"
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
